import { InfoIcon, WarningCircleIcon, WarningIcon } from '@phosphor-icons/react'
import type { CSSProperties, ReactNode } from 'react'
import { cx } from './cx.ts'
import { Button } from './Button.tsx'
import { CopyText } from './Display.tsx'

export interface EmptyStateProps {
  /** Terse mono label, e.g. "NO ENTRIES". */
  label?: string
  title: ReactNode
  children?: ReactNode
  actions?: ReactNode
  meta?: ReactNode
  /** No dashed frame (inside tables and panels). */
  flush?: boolean
  className?: string
}

/** Empty is a state with a reason and a next step, never a blank area. */
export function EmptyState({ label = 'Nothing here yet', title, children, actions, meta, flush, className }: EmptyStateProps) {
  return (
    <section className={cx('rt-state', className)} data-flush={flush ? '' : undefined}>
      <span className="rt-label">{label}</span>
      <h3 className="rt-state__title">{title}</h3>
      {children ? <div className="rt-state__body">{children}</div> : null}
      {meta ? <div className="rt-state__meta rt-mono">{meta}</div> : null}
      {actions ? <div className="rt-state__actions">{actions}</div> : null}
    </section>
  )
}

export interface ErrorStateProps {
  title?: ReactNode
  detail?: ReactNode
  code?: string
  requestId?: string | null
  status?: number
  onRetry?: () => void
  flush?: boolean
  className?: string
}

/** A failed request, with what the person can do and the ids support needs. */
export function ErrorState({ title = 'This didn’t load', detail, code, requestId, status, onRetry, flush, className }: ErrorStateProps) {
  return (
    <section className={cx('rt-state', className)} data-tone="fault" data-flush={flush ? '' : undefined} role="alert">
      <span className="rt-label rt-fault-text">Error{code ? ` · ${code}` : ''}{status ? ` · ${status}` : ''}</span>
      <h3 className="rt-state__title">{title}</h3>
      {detail ? <div className="rt-state__body">{detail}</div> : null}
      {requestId ? (
        <div className="rt-state__meta rt-mono">
          <span>request</span>
          <CopyText value={requestId} />
        </div>
      ) : null}
      {onRetry ? (
        <div className="rt-state__actions">
          <Button size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </section>
  )
}

export interface UnavailableStateProps {
  /** What this surface will do once its service is live. */
  title: ReactNode
  children?: ReactNode
  /** e.g. `GET /api/platform/v1/library` */
  endpoint?: string
  /** e.g. 404, or 'unreachable' */
  status?: string | number
  onRetry?: () => void
  flush?: boolean
  className?: string
}

/** Honest pending state: the surface is designed, the backend isn't live yet. Never mock data. */
export function UnavailableState({ title, children, endpoint, status, onRetry, flush, className }: UnavailableStateProps) {
  return (
    <section className={cx('rt-state', className)} data-flush={flush ? '' : undefined}>
      <span className="rt-label">Not connected yet</span>
      <h3 className="rt-state__title">{title}</h3>
      {children ? <div className="rt-state__body">{children}</div> : null}
      {endpoint ? (
        <div className="rt-state__meta rt-mono">
          <span>{endpoint}</span>
          {status !== undefined ? <span>→ {status}</span> : null}
        </div>
      ) : null}
      {onRetry ? (
        <div className="rt-state__actions">
          <Button size="sm" variant="secondary" onClick={onRetry}>
            Check again
          </Button>
        </div>
      ) : null}
    </section>
  )
}

export function Notice({ tone = 'neutral', title, children, icon, actions, className }: { tone?: 'neutral' | 'warn' | 'fault' | 'live'; title?: ReactNode; children?: ReactNode; icon?: ReactNode; actions?: ReactNode; className?: string }) {
  const glyph = icon ?? (tone === 'warn' ? <WarningIcon weight="bold" /> : tone === 'fault' ? <WarningCircleIcon weight="bold" /> : <InfoIcon />)
  return (
    <div className={cx('rt-notice', className)} data-tone={tone} role={tone === 'fault' ? 'alert' : undefined}>
      {glyph}
      <div className="rt-notice__body">
        {title ? <span className="rt-notice__title">{title}</span> : null}
        {children ? <span className="rt-notice__text">{children}</span> : null}
      </div>
      {actions}
    </div>
  )
}

export function Skeleton({ width = '100%', height = 12, className, style }: { width?: number | string; height?: number; className?: string; style?: CSSProperties }) {
  return <span className={cx('rt-skel', className)} style={{ width, height, ...style }} aria-hidden />
}

/** Rows of skeleton bars sized like table rows, announced once as loading. */
export function SkeletonRows({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div role="status" aria-label="Loading" style={{ display: 'flex', flexDirection: 'column' }}>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} style={{ display: 'grid', gridTemplateColumns: `2fr repeat(${columns - 1}, 1fr)`, gap: 16, alignItems: 'center', height: 'var(--rt-row)', padding: '0 16px', borderBottom: '1px solid var(--rt-line)' }}>
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} width={`${55 + ((r * 7 + c * 13) % 40)}%`} height={10} />
          ))}
        </div>
      ))}
    </div>
  )
}
